// willow-saas's public forms (`src/handlers/forms.rs`), reached through the
// same-origin `/api` proxy. The website posts to the same endpoint. saas
// stores a call request and emails it to the team, with Reply-To set to the
// sender, so answering the email answers them.

import { capitalize } from './errors';

/** The body `POST /forms/call-request` takes. saas requires every field but
 *  the honeypot, and refuses blank ones. */
export interface CallRequest {
  first_name: string;
  last_name: string;
  email: string;
  company: string;
  message: string;
  /** Honeypot, hidden from people. saas answers a filled one with the usual
   *  success and stores nothing. */
  website: string;
  /** Sent like the sign-in forms send it. saas ignores unknown fields; the
   *  forms endpoint doesn't verify Turnstile today (honeypot and rate limits). */
  turnstile_token?: string;
}

/** A refused or failed submission. `message` is shown as is. */
export class FormError extends Error {}

/** A sentence for the page: capitalized and ending in a full stop. saas sends
 *  lowercase fragments ("first name is required"). */
function sentence(msg: string): string {
  const s = capitalize(msg);
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

export async function sendCallRequest(req: CallRequest): Promise<void> {
  let res: Response;
  try {
    res = await fetch('/api/forms/call-request', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...req, page_url: window.location.href }),
    });
  } catch {
    throw new FormError('Network error. Try again in a moment.');
  }
  if (res.ok) return;
  if (res.status === 429) {
    throw new FormError('Too many requests. Wait a few minutes and try again.');
  }
  const body: unknown = await res.json().catch(() => null);
  const error = (body as { error?: unknown } | null)?.error;
  if (res.status === 400 && typeof error === 'string' && error.trim()) {
    throw new FormError(sentence(error));
  }
  throw new FormError('Something went wrong. Try again in a moment.');
}
